/**
 * A panel of named catalogue fields, read and edited the dossier's way (UI-11).
 *
 * Twenty catalogue fields — the equity-and-returns terms (peak equity,
 * preferred return, IRR hurdle, promote, sale and refinance proceeds, exit
 * year), EBITDA, renovated units, the note name, the rate lock, the deal
 * comment, the secondary underwriter, the internal contact and the quote and
 * deposit dates — were reachable on the Audit tab alone, where a reader after
 * them opened All fields and searched. Two panels put them on the page that
 * discusses them: Returns on Economics, Workflow on the Overview. Each value is
 * the field it names, editable in place once unlocked, through the same draft
 * and Save as the rest of the dossier.
 */
import { formatFieldValue } from './format'
import { EditableValue } from './EditableValue'
import { useDossierEdit } from './editContext'
import { ValueStateChip } from './valueStateChip'

/** The Returns panel (Economics): equity-and-returns terms the Economics
 *  charts do not print, plus EBITDA and the renovated-unit count. */
export const RETURNS_FIELDS: readonly string[] = [
  'peak_equity',
  'preferred_return',
  'irr_hurdle',
  'promote',
  'sale_proceeds',
  'disposition_fee',
  'exit_year',
  'interest_reserve',
  'refi_proceeds',
  'refi_dscr',
  'ebitda',
  'renovated_units',
]

/** The Workflow panel (Overview): the desk's own notes, people and clocks. */
export const WORKFLOW_FIELDS: readonly string[] = [
  'deal_comment',
  'note_name',
  'interest_rate_locked',
  'secondary_underwriter',
  'internal_contact',
  'internal_contact_email',
  'quote_letter_date',
  'deposit_wire_date',
]

function held(value: unknown): boolean {
  return value !== null && value !== undefined && value !== ''
}

export function CatalogueFieldsPanel({
  title,
  fields,
  defaultOpen = true,
}: {
  title: string
  /** Catalogue field names, in the order they read. A name the catalogue does not define is skipped. */
  fields: readonly string[]
  /** Collapsed by default where the page must fit one viewport (the Overview). */
  defaultOpen?: boolean
}) {
  const edit = useDossierEdit()
  const rows = fields
    .map((name) => edit.defs.find((def) => def.name === name))
    .filter((def): def is NonNullable<typeof def> => def != null)
  if (rows.length === 0) return null
  const recorded = rows.filter((def) => held(edit.effectiveValue(def.name))).length

  return (
    <details
      className="shrink-0 rounded-lg border border-stone-800/80 bg-black/20"
      open={defaultOpen}
      data-testid={`catalogue-fields-${title.toLowerCase()}`}
    >
      <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-stone-400">
        <span>{title}</span>
        <span className="text-stone-600 normal-case tracking-normal">
          {recorded} of {rows.length} recorded
        </span>
      </summary>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1 px-3 pb-2 sm:grid-cols-2 xl:grid-cols-3">
        {rows.map((def) => {
          const value = edit.effectiveValue(def.name)
          const text = formatFieldValue(value, def.kind, def.name) || '—'
          return (
            <div key={def.name} className="flex items-baseline justify-between gap-2 text-[11px]" data-field={def.name}>
              <dt className="shrink-0 text-stone-500">{def.label}</dt>
              <dd className={`min-w-0 truncate text-right font-mono ${held(value) ? 'text-stone-200' : 'text-stone-600'}`}>
                <EditableValue
                  name={def.name}
                  label={def.label}
                  display={text}
                  marks={<ValueStateChip source={edit.valueSource?.(def.name) ?? null} />}
                />
              </dd>
            </div>
          )
        })}
      </dl>
    </details>
  )
}
